import { useCallback, useEffect, useRef, useState } from "react";
import betaAPI from "v2/api";
import { useCaseCommand, useCaseStream } from "hooks/useCaseDashboard";
import { isUpdating } from "utils/caseSoftware";

// Software updates for one case (the Software tab). The flow:
//   latest (API, admins) → download to this computer (main process, sha256)
//   → upload to the case over SSH → `update <label>` starts the case's
//   updater → follow `update-status` + `update-log --follow` → outcome.
// A rollback skips straight to the last two steps.

const api = () => window.electronAPI?.caseDashboard;

/** Superadmins (or the dev page's simulated case) may update cases. */
export const canUpdateCases = () => {
  if (api()?.isSimulated) return true;
  try {
    return (
      JSON.parse(localStorage.getItem("user") || "null")?.is_superadmin === true
    );
  } catch {
    return false;
  }
};

/** The newest release on a channel, from the API (or the simulated case). */
function useLatestRelease(channel, enabled) {
  const [state, setState] = useState({
    data: null,
    error: null,
    loading: false,
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    const load = api()?.latestRelease
      ? api().latestRelease(channel)
      : betaAPI
          .getCaseSoftwareLatest({ channel })
          .then((res) => res.data)
          .catch((e) => {
            if (e.status === 404) return null; // nothing released yet
            throw e;
          });
    Promise.resolve(load)
      .then(
        (data) => !cancelled && setState({ data, error: null, loading: false })
      )
      .catch(
        (e) =>
          !cancelled &&
          setState({ data: null, error: e.message || "error", loading: false })
      );
    return () => {
      cancelled = true;
    };
  }, [channel, enabled, nonce]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);
  return { ...state, refresh };
}

/** Releases downloaded on this computer, and downloading more. */
function useDownloads() {
  const [releases, setReleases] = useState([]);
  const [progress, setProgress] = useState({}); // label → { received, total }
  const [errors, setErrors] = useState({}); // label → message

  const reload = useCallback(async () => {
    const res = await api()?.releaseList?.();
    if (res?.ok) setReleases(res.releases);
  }, []);

  useEffect(() => {
    reload();
    return api()?.onReleaseProgress?.((p) =>
      setProgress((prev) => ({ ...prev, [p.label]: p }))
    );
  }, [reload]);

  const download = useCallback(
    async (release) => {
      const label = release.tag;
      setErrors((prev) => ({ ...prev, [label]: null }));
      setProgress((prev) => ({
        ...prev,
        [label]: { label, received: 0, total: release.artifacts?.bundle?.size },
      }));
      const res = await api().releaseDownload(release);
      setProgress((prev) => {
        const next = { ...prev };
        delete next[label];
        return next;
      });
      if (!res?.ok) {
        setErrors((prev) => ({
          ...prev,
          [label]: res?.error?.message || "download failed",
        }));
      }
      await reload();
      return res;
    },
    [reload]
  );

  return { releases, progress, errors, download, reload };
}

export function useCaseSoftware(caseId, { enabled, isAdmin }) {
  const [channel, setChannel] = useState("production");
  const [sending, setSending] = useState(null); // { label, sent, total }
  const [startError, setStartError] = useState(null);

  // Poll fast only while a run is going.
  const [statusEvery, setStatusEvery] = useState(15000);
  const status = useCaseCommand(caseId, "update-status", statusEvery, enabled);
  const updating = isUpdating(status.data);
  useEffect(() => {
    setStatusEvery(updating && !sending ? 2500 : sending ? 0 : 15000);
  }, [updating, sending]);
  const versions = useCaseCommand(caseId, "version", 30000, enabled);
  const history = useCaseCommand(caseId, "update-history", 0, enabled);
  const latest = useLatestRelease(channel, enabled && isAdmin);
  const downloads = useDownloads();
  const log = useCaseStream(caseId, { maxLines: 1000 });

  useEffect(
    () =>
      api()?.onUploadProgress?.((p) => {
        if (p.caseId === caseId)
          setSending({ label: p.label, sent: p.sent, total: p.total });
      }),
    [caseId]
  );

  // Follow the updater's log whenever a run is going (also when the tab opens
  // mid-update, e.g. one started from another computer or the terminal).
  const { running: following, start: follow } = log;
  useEffect(() => {
    if (updating && !following) follow("update-log --follow");
  }, [updating, following, follow]);

  // When a run ends: what's installed and the history changed. Reconnect
  // first: the update may have pointed the dashboard key at the new
  // release's manage.py, and SSH only reads that when a connection opens.
  const wasUpdating = useRef(false);
  const { refresh: refreshVersions } = versions;
  const { refresh: refreshHistory } = history;
  useEffect(() => {
    if (wasUpdating.current && !updating) {
      Promise.resolve(api()?.refreshConnection?.(caseId))
        .catch(() => {})
        .finally(() => {
          refreshVersions();
          refreshHistory();
        });
    }
    wasUpdating.current = updating;
  }, [caseId, updating, refreshVersions, refreshHistory]);

  const { refresh: refreshStatus } = status;
  const startUpdate = useCallback(
    async (label) => {
      setStartError(null);
      setSending({ label, sent: 0, total: null });
      const up = await api().upload(caseId, label);
      if (!up?.ok) {
        setSending(null);
        setStartError(up?.error?.message || up?.error || "upload failed");
        return;
      }
      const res = await api().run(caseId, `update ${label}`);
      setSending(null);
      if (!res?.ok) {
        setStartError(res?.error || "the case didn't start the update");
        return;
      }
      follow("update-log --follow");
      refreshStatus();
    },
    [caseId, follow, refreshStatus]
  );

  const rollback = useCallback(async () => {
    setStartError(null);
    const res = await api().run(caseId, "rollback");
    if (!res?.ok) {
      setStartError(res?.error || "the case didn't start the rollback");
      return;
    }
    follow("update-log --follow");
    refreshStatus();
  }, [caseId, follow, refreshStatus]);

  return {
    channel,
    setChannel,
    versions,
    status,
    history,
    latest,
    downloads,
    log,
    sending,
    startError,
    updating: updating || !!sending,
    startUpdate,
    rollback,
  };
}
